import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { CodeBox, CodeTabs } from "@/components/embed/CodeBox";
import {
  API_SNIPPET,
  EMBEDS,
  SITE,
  aiPromptFor,
  iframeSnippetFor,
  reactSnippetFor,
  snippetFor,
  type EmbedDef,
} from "@/lib/embed/registry";

const TITLE = "Free Embeddable Widgets & API — DrHint Developer Hub";
const DESC =
  "Paste one line of code and get a complete ready-made page: live gold rate, silver rate, gold calculator, age, pregnancy, BMI and zakat calculators. Works on WordPress, Blogger, Next.js, Lovable, Vercel and custom sites.";

export const Route = createFileRoute("/widgets")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/widgets` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/widgets` }],
  }),
  component: WidgetsHub,
});

const GROUPS: { key: EmbedDef["category"]; title: string; sub: string }[] = [
  { key: "gold", title: "Gold rate page widgets", sub: "Full gold pages with chart, karat table and calculator." },
  { key: "silver", title: "Silver rate page widgets", sub: "Live silver by tola, gram, 10 gram and kilo." },
  { key: "calculator", title: "Calculator page widgets", sub: "Ready-made calculator pages for your audience." },
];

function WidgetsHub() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: TITLE,
    description: DESC,
    url: `${SITE}/widgets`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: EMBEDS.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: e.name,
        url: `${SITE}/embed/page/${e.id}`,
      })),
    },
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-500">
          Developer Hub · Free forever
        </div>
        <h1 className="mt-3 text-3xl md:text-4xl font-bold">
          Embed a complete live page on your website with one line of code
        </h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          These are not tiny badges. Each embed renders a full ready-made page inside your site — live chart, karat-wise
          prices, unit tables, a working calculator and city links — and it refreshes automatically every 10 minutes.
          Once you paste the code, your page stays up to date forever without you touching it again. Free for personal
          and commercial use; the visible source credit link must stay.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3 text-sm">
          {[
            ["1. Pick a widget", "Gold, silver or calculator — 10 ready pages."],
            ["2. Copy the code", "One universal snippet, auto-resizing."],
            ["3. Paste anywhere", "WordPress, Blogger, Next.js, Lovable, HTML."],
          ].map(([h, s]) => (
            <div key={h} className="rounded-xl border border-border bg-card/40 p-4">
              <div className="font-bold">{h}</div>
              <div className="text-muted-foreground text-xs mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-6">
        <div className="rounded-2xl border border-border bg-card/40 p-5">
          <h2 className="text-lg font-bold">Quick start (works on every platform)</h2>
          <p className="mt-1 mb-3 text-xs text-muted-foreground">
            Paste this once. The loader script finds every <code>data-drhint-embed</code> block on the page, injects the
            live widget and resizes it to fit — no fixed heights, no scrollbars.
          </p>
          <CodeBox
            label="Universal snippet"
            code={`<div data-drhint-embed="gold-pakistan"></div>\n<script src="${SITE}/embed.js" async></script>`}
          />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-6">
        <div className="rounded-2xl border border-border bg-card/40 p-5">
          <h2 className="text-lg font-bold">Building with an AI (Lovable, v0, Bolt, Cursor)?</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Every widget below has an <strong>AI prompt</strong> tab. Copy that prompt, paste it into your AI builder,
            and it will create a complete page around the live widget — including the required credit link. The AI does
            not need to scrape anything: the prompt already contains the exact embed code and URLs.
          </p>
        </div>
      </section>

      {GROUPS.map((g) => (
        <section key={g.key} className="mx-auto max-w-5xl px-4 pb-8">
          <h2 className="text-xl md:text-2xl font-bold">{g.title}</h2>
          <p className="text-sm text-muted-foreground mb-4">{g.sub}</p>
          <div className="space-y-6">
            {EMBEDS.filter((e) => e.category === g.key).map((e) => (
              <WidgetCard key={e.id} embed={e} />
            ))}
          </div>
        </section>
      ))}

      <section className="mx-auto max-w-5xl px-4 pb-10">
        <h2 className="text-xl md:text-2xl font-bold">JSON API for developers</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Prefer raw numbers? Our public endpoint returns live gold spot, USD/PKR, USD/INR and computed local prices with
          CORS enabled — no API key, no signup.
        </p>
        <div className="space-y-3">
          <CodeBox label="Endpoint" code={`GET ${SITE}/api/public/hooks/gold-rates`} />
          <CodeBox label="JavaScript" code={API_SNIPPET} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Full API documentation:{" "}
          <Link to="/live-gold-rate-api" className="text-amber-500 underline">
            live gold rate API docs
          </Link>
          . Attribution link to drhint.com is required when you display our data.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-4 pb-14">
        <h2 className="text-xl md:text-2xl font-bold mb-3">Platform install guides</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            ["WordPress (Gutenberg)", "Add a Custom HTML block, paste the universal snippet, publish. In Classic Editor switch to the Text tab first."],
            ["Blogger / Blogspot", "Blogger strips some scripts — use the iFrame tab code instead of the universal snippet."],
            ["Next.js / Vercel", "Use the React tab. Put the component in your page; no client-side data fetching needed."],
            ["Lovable / React apps", "Paste the React snippet as a component and drop it into any route."],
            ["Wix / Squarespace / Webflow", "Insert an Embed / Custom Code element and paste the iFrame code."],
            ["Plain HTML / custom site", "Paste the universal snippet anywhere inside <body>. Keep the credit paragraph visible."],
          ].map(([h, s]) => (
            <div key={h} className="rounded-xl border border-border bg-card/40 p-4">
              <div className="font-bold text-sm">{h}</div>
              <div className="text-xs text-muted-foreground mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function WidgetCard({ embed }: { embed: EmbedDef }) {
  return (
    <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 via-card/40 to-background p-5">
      <h3 className="text-base md:text-lg font-bold">{embed.name}</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-3">{embed.blurb}</p>

      <CodeTabs
        tabs={[
          {
            id: "universal",
            label: "Universal (recommended)",
            code: snippetFor(embed),
            note: "Auto-resizes to content height. Works on WordPress, custom HTML and any site that allows scripts.",
          },
          {
            id: "iframe",
            label: "iFrame only",
            code: iframeSnippetFor(embed),
            note: "For Blogger, Wix and editors that strip <script> tags. Fixed height.",
          },
          {
            id: "react",
            label: "React / Next.js",
            code: reactSnippetFor(embed),
            note: "Drop-in component for Next.js, Vercel, Lovable and any React app.",
          },
          {
            id: "ai",
            label: "AI prompt (Lovable / v0)",
            code: aiPromptFor(embed),
            note: "Paste this prompt into Lovable, v0, Bolt or Cursor and it will build the whole page around this widget for you.",
          },
        ]}
      />

      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-semibold text-amber-500">Live preview</summary>
        <iframe
          src={`${SITE}/embed/page/${embed.id}`}
          title={`${embed.name} preview`}
          loading="lazy"
          className="mt-2 w-full rounded-lg border border-border"
          style={{ height: Math.min(embed.height, 700) }}
        />
      </details>
    </div>
  );
}
